import React from 'react';
import { 
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Drawer,
  Radio,
  RadioGroup,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { UsersFilterState } from '../hooks/useUsersFilter';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  tempState: UsersFilterState;
  expandedAccordions: string[];
  setExpandedAccordions: (expanded: string[]) => void;
  handleEnabledChange: (value: 'all' | 'enabled' | 'disabled') => void;
  handleUsernameFilterChange: (value: string) => void;
  applyFilters: () => void;
  clearFilters: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  tempState,
  expandedAccordions,
  setExpandedAccordions,
  handleEnabledChange,
  handleUsernameFilterChange,
  applyFilters,
  clearFilters
}) => {
  const theme = useTheme();

  // Handle accordion expand/collapse
  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(isExpanded
      ? [...expandedAccordions, panel]
      : expandedAccordions.filter(item => item !== panel)
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 340 },
          maxWidth: '100%',
          borderTopLeftRadius: { xs: 0, sm: 16 },
          borderBottomLeftRadius: { xs: 0, sm: 16 },
          boxShadow: '0 0 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <Box 
        sx={{ 
          p: 2.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          pb={2}
          borderBottom={`1px solid ${alpha(theme.palette.divider, 0.7)}`}
        >
          <Box display="flex" alignItems="center">
            <FilterAltIcon 
              color="primary" 
              sx={{ 
                mr: 1.5, 
                fontSize: '1.5rem' 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Filter Users
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Accordion 
            expanded={expandedAccordions.includes('username')}
            onChange={handleAccordionChange('username')}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.includes('username') ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.includes('username') ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.includes('username') ? theme.palette.primary.main : 'inherit'
                }}
              >
                Username
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by username"
                value={tempState.username}
                onChange={(e) => handleUsernameFilterChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    }
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
          
          <Accordion 
            expanded={expandedAccordions.includes('status')} 
            onChange={handleAccordionChange('status')}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.includes('status') ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.includes('status') ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.includes('status') ? theme.palette.primary.main : 'inherit'
                }}
              >
                User Status
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RadioGroup 
                value={tempState.enabled} 
                onChange={(e) => handleEnabledChange(e.target.value as 'all' | 'enabled' | 'disabled')}
              >
                <FormControlLabel 
                  value="all" 
                  control={
                    <Radio 
                      color="primary" 
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20
                        }
                      }} 
                    />
                  } 
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All
                    </Typography>
                  }
                  sx={{ mb: 0.5 }}
                />
                <FormControlLabel 
                  value="enabled" 
                  control={
                    <Radio 
                      color="primary" 
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20
                        }
                      }} 
                    />
                  } 
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Enabled
                    </Typography>
                  } 
                  sx={{ mb: 0.5 }}
                />
                <FormControlLabel 
                  value="disabled" 
                  control={
                    <Radio 
                      color="primary" 
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20
                        }
                      }} 
                    />
                  } 
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Disabled
                    </Typography>
                  } 
                />
              </RadioGroup>
            </AccordionDetails>
          </Accordion>
        </Box>
      
        <Box mt="auto">
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            onClick={applyFilters}
            startIcon={<FilterAltIcon />}
            sx={{ 
              py: 1,
              mb: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 15px rgba(0,0,0,0.15)'
              }
            }}
          >
            Apply Filters
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            fullWidth 
            onClick={clearFilters} 
            startIcon={<CleaningServicesIcon />}
            sx={{ 
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.08)
              }
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;